import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './pages/Login';
import StaffLogin from './pages/StaffLogin';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import Payments from './pages/Payments';
import Staff from './pages/Staff';
import Expenses from './pages/Expenses';
import Management from './pages/Management';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return <>{children}</>;
};

// RBAC Protected Route - checks if user can access specific section
const RBACRoute: React.FC<{ children: React.ReactNode; section: string }> = ({ children, section }) => {
  const { isAuthenticated, loading, canAccess } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!canAccess(section)) {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
};

// Studio Owner Only Route - restricts access to studio owners only
const StudioOwnerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, isStudioOwner } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!isStudioOwner) {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
};

// Public Route component (redirects to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" />;
  }
  
  return <>{children}</>;
};

const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('super_access_token');
  if (!token) {
    return <Navigate to="/super-admin/login" />;
  }
  return <>{children}</>;
};

function App() {
  useEffect(() => {
    const storedTheme = localStorage.getItem('soms_theme');
    const storedAccent = localStorage.getItem('soms_accent') || 'indigo';
    const storedDensity = localStorage.getItem('soms_density') || 'comfortable';

    if (storedTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    document.documentElement.setAttribute('data-accent', storedAccent);
    document.documentElement.setAttribute('data-density', storedDensity);
  }, []);

  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/super-admin/login" element={<SuperAdminLogin />} />
          <Route path="/super-admin" element={
            <SuperAdminRoute>
              <SuperAdminDashboard />
            </SuperAdminRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/staff-login" element={
            <PublicRoute>
              <StaffLogin />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/projects" element={
            <RBACRoute section="projects">
              <Projects />
            </RBACRoute>
          } />
          <Route path="/payments" element={
            <RBACRoute section="payments">
              <Payments />
            </RBACRoute>
          } />
          <Route path="/staff" element={
            <RBACRoute section="staff">
              <Staff />
            </RBACRoute>
          } />
          <Route path="/expenses" element={
            <RBACRoute section="expenses">
              <Expenses />
            </RBACRoute>
          } />
          <Route path="/management" element={
            <StudioOwnerRoute>
              <Management />
            </StudioOwnerRoute>
          } />
          <Route path="/" element={<Navigate to="/dashboard" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
